import { useEffect, useState } from "react";
import { Maximize2 } from "lucide-react";
import FileChip from "@photon/chat-react/file-chip";
import UserAuthStore from "../../store/userStore";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import MediaModal from "../Common/MediaModal";
import CustomTooltip from "../Tooltip/CustomTooltip";
import useResolvedMediaUrlStore from "../../store/useResolvedMediaUrlStore";
import { useTheme } from "../../context/ThemeProvider";
import logger from "../../services/Logger";

// Simple audio player with loop button
const StorageImage = ({ src, alt }) => {
  const [showModal, setShowModal] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const assetMimeType = asset?.mime_type;
  const { theme } = useTheme();
  const setLoadedUrl = useResolvedMediaUrlStore(
    (state) => state.setLoadedUrl
  );
  const loadedUrl = useResolvedMediaUrlStore((state) => state.urls[src]);

  // Helper function to determine file type
  const getFileType = (filePath, mimeType) => {
    if (mimeType?.startsWith('video/')) return 'video';
    if (mimeType?.startsWith('audio/')) return 'audio';
    if (mimeType?.startsWith('image/')) return 'image';
    const extension = filePath.toLowerCase().split('.').pop();
    if (['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(extension)) {
      return 'video';
    }
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(extension)) {
      return 'audio';
    }
    return 'image';
  };

  const isAsset = src.startsWith('asset_');
  const fileType = getFileType(src, assetMimeType);

  useEffect(() => {
    let retries = 0;
    let timeoutId = null;

    const getUrl = async () => {
      try {
        const resolved = isAsset
          ? await mediaUrlAdapter.resolveAsset(src)
          : { url: await mediaUrlAdapter.getUrl(src, UserAuthStore.getState().orgId) };
        const url = resolved?.url;
        if (url) {
          if (isAsset) setAsset(resolved);
          setLoadedUrl(src, url);
        } else if (retries < 3) {
          retries++;
          timeoutId = setTimeout(getUrl, 2000);
        }
      } catch (err) {
        // Previously unhandled — a rejected getUrl() call (network error, 403, etc.)
        // never reached the falsy-url retry branch below, so it silently left this
        // stuck on the loading skeleton forever with an unhandled promise rejection.
        if (retries < 3) {
          retries++;
          timeoutId = setTimeout(getUrl, 2000);
        } else {
          logger.error("StorageImage", "Failed to resolve media URL", { err: err?.message, src });
        }
      }
    };

    if (!loadedUrl || (isAsset && !assetMimeType)) {
      getUrl();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [src, loadedUrl, setLoadedUrl, isAsset, assetMimeType]);

  const openImageModal = () => setShowModal(true);
  const closeImageModal = () => setShowModal(false);

  if (loadedUrl && (!isAsset || asset)) {
    // Render video player for video files
    if (fileType === 'video') {
      return (
        <>
          <div className="relative group max-w-[600px] rounded-lg overflow-hidden my-2">
            {imgLoading && (
              <div className={`w-full aspect-video rounded-lg animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
            )}
            <video
              src={loadedUrl}
              controls
              onLoadedData={() => setImgLoading(false)}
              className={`w-full h-auto rounded-lg${imgLoading ? ' opacity-0 absolute inset-0' : ''}`}
              preload="metadata"
            />
            {/* Fullscreen button — same style as image */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <CustomTooltip content="Fullscreen" side="top" align="center">
                <button
                  onClick={openImageModal}
                  className="flex items-center p-2 bg-fill-white dark:bg-fill-editor-surface border-[0.5px] dark:border-edge-editor-border border-edge-border text-ink-ink dark:text-ink-white rounded-lg hover:bg-fill-gray-100 dark:hover:bg-fill-editor-hover transition-colors"
                  aria-label="View fullscreen"
                >
                  <Maximize2 className="w-3.5 h-3.5 hover:scale-110 transition-transform" />
                </button>
              </CustomTooltip>
            </div>
          </div>

          {showModal && (
            <MediaModal src={loadedUrl} alt={alt} type="video" onClose={closeImageModal} />
          )}
        </>
      );
    }

    // Render audio player for audio files
    if (fileType === 'audio') {
      return (
        <div className="my-4">
          <div className={`
            inline-block p-3 rounded-xl border-2
            ${theme === 'dark'
              ? 'border-edge-blue-300/40 shadow-sm'
              : 'border-edge-blue-300/60 shadow-sm'
            }
          `}>
            <audio controls style={{ minWidth: '300px' }} className="rounded-md">
              <source src={loadedUrl} type="audio/mpeg" />
              <source src={loadedUrl} type="audio/wav" />
              <source src={loadedUrl} type="audio/ogg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      );
    }

    if (isAsset && asset) {
      return (
        <>
          <FileChip
            filename={asset.filename}
            extension={asset.filename.split(".").pop().toUpperCase()}
            kind="image"
            url={loadedUrl}
            onPreview={openImageModal}
          />
          {showModal && (
            <MediaModal src={loadedUrl} alt={asset.filename} onClose={closeImageModal} />
          )}
        </>
      );
    }

    // Render image with modal for image files (default behavior)
    return (
      <>
        <div className="relative group max-w-[400px] rounded-lg overflow-hidden">
          {imgLoading && (
            <div className={`w-full aspect-video rounded-lg animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
          )}
          <img
            src={loadedUrl}
            alt={alt}
            onLoad={() => setImgLoading(false)}
            className={`h-full cursor-pointer hover:opacity-90 transition-opacity${imgLoading ? ' opacity-0 absolute inset-0' : ''}`}
            onClick={openImageModal}
          />
          {/* Fullscreen button - matches VizActionBar style */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <CustomTooltip content="Fullscreen" side="top" align="center">
              <button
                onClick={openImageModal}
                className="flex items-center p-2 bg-fill-white dark:bg-fill-editor-surface border-[0.5px] dark:border-edge-editor-border border-edge-border text-ink-ink dark:text-ink-white rounded-lg hover:bg-fill-gray-100 dark:hover:bg-fill-editor-hover transition-colors"
                aria-label="View fullscreen"
              >
                <Maximize2 className="w-3.5 h-3.5 hover:scale-110 transition-transform" />
              </button>
            </CustomTooltip>
          </div>
        </div>

        {showModal && (
          <MediaModal src={loadedUrl} alt={alt} onClose={closeImageModal} />
        )}
      </>
    );
  }

  // Loading state — per-type skeleton
  if (fileType === 'video') {
    return (
      <div className={`max-w-[600px] w-full aspect-video rounded-lg animate-pulse my-2 ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
    );
  }

  if (fileType === 'audio') {
    return (
      <div className="my-4">
        <div className={`inline-flex items-center space-x-3 p-3 rounded-xl border-2 ${
          theme === 'dark' ? 'border-edge-blue-300/40' : 'border-edge-blue-300/60'
        }`}>
          <div className={`h-8 w-8 rounded-full animate-pulse flex-shrink-0 ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-300'}`} />
          <div className={`h-8 w-[300px] rounded animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-300'}`} />
        </div>
      </div>
    );
  }

  // image (default)
  return (
    <div role={isAsset ? 'status' : undefined} aria-label={isAsset ? 'Loading attachment' : undefined} className={`${isAsset ? 'w-[46px] h-[46px] rounded-[10px]' : 'max-w-[400px] w-full aspect-video rounded-lg'} animate-pulse ${theme === 'dark' ? 'bg-fill-gray-700' : 'bg-fill-gray-200'}`} />
  );
};

export default StorageImage;
